import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Box
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Document, Page, pdfjs } from 'react-pdf';
import SignatureCanvas from 'react-signature-canvas';
import cargoesLogo from '../../images/Cargoes.png';
import { toast } from 'react-toastify';
import ESignService from '../../services/ESignService';
import SignaturePageNavigation from './SignaturePageNavigation';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5'
  },
  appBar: {
    backgroundColor: theme.palette.primary.main,
    height: 60,
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px'
  },
  logo: {
    height: 40
  },
  title: {
    marginLeft: 16,
    color: 'white',
    fontWeight: 600
  },
  container: {
    padding: theme.spacing(3)
  },
  paper: {
    padding: 0,
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: '1px solid #e0e0e0'
  },
  buttons: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  documentContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    background: '#fafafa',
    padding: '24px 0',
    maxHeight: 'calc(100vh - 200px)', // Add maximum height
    overflowY: 'auto',
  },
  pdfWrapper: {
    position: 'relative',
    display: 'inline-block'
  },
  fieldOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    pointerEvents: 'none',
    zIndex: 10
  },
  signatureField: {
    position: 'absolute',
    border: '2px solid #ffd700',
    backgroundColor: 'rgba(255, 215, 0, 0.05)',
    cursor: 'pointer',
    pointerEvents: 'all',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  signatureImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  signatureModal: {
    '& .MuiDialog-paper': {
      width: '400px',
      maxWidth: '90vw'
    }
  },
  signatureCanvas: {
    border: '1px solid #e0e0e0',
    backgroundColor: '#fff',
    marginTop: theme.spacing(2)
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh'
  },
  pdfWrapper: {
    position: 'relative',
    display: 'inline-block',
    margin: '0 auto',               // Center the content
  },
  pdfPage: {
    marginBottom: theme.spacing(2), // Add space between pages
    '&:last-child': {
      marginBottom: 0
    }
  },
  signatureNav: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[100],
    borderBottom: `1px solid ${theme.palette.divider}`,
  }
}));

const SigningPage = () => {
  const classes = useStyles();
  const { token } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [documentData, setDocumentData] = useState(null);
  const [error, setError] = useState(null);
  const [signatures, setSignatures] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [typedName, setTypedName] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [pdfWidth, setPdfWidth] = useState(null);
  const [pdfHeight, setPdfHeight] = useState(null);
  const [numPages, setNumPages] = useState(null);
  // User-adjustable font size for signature
  const [signatureFontSize, setSignatureFontSize] = useState(48);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionComment, setRejectionComment] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const signatureCanvasWidth = 400;
  const signatureCanvasHeight = 150;
  const signatureRef = useRef();

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        const response = await ESignService.getDocumentForSigning(token);
        setDocumentData(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load document');
        setLoading(false);
      }
    };
    fetchDocument();
  }, [token]);

  const handleFieldClick = (field) => {
    setActiveField(field);
    setSignatureModalOpen(true);
    if (signatureRef.current) signatureRef.current.clear();
    setTabValue(0);
    setSignatureFontSize(48);
  };

  const handleCloseSignatureModal = () => {
    setSignatureModalOpen(false);
    setActiveField(null);
  };

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handleClearSignature = () => {
    if (tabValue === 1 && signatureRef.current) signatureRef.current.clear();
    else if (tabValue === 0) setTypedName('');
  };

  const handleApplySignature = () => {
    if (!activeField) return;
    let signatureData;
    if (tabValue === 1) {
      if (signatureRef.current && !signatureRef.current.isEmpty()) {
        signatureData = signatureRef.current.toDataURL('image/png').split(',')[1];
      } else {
        toast.error('Please draw your signature');
        return;
      }
    } else {
      if (!typedName.trim()) {
        toast.error('Please enter your name');
        return;
      }
      // Use user-selected font size
      const canvas = document.createElement('canvas');
      canvas.width = signatureCanvasWidth;
      canvas.height = signatureCanvasHeight;
      const ctx = canvas.getContext('2d');
      ctx.font = `italic ${signatureFontSize}px serif`;
      ctx.fillStyle = '#000';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);
      signatureData = canvas.toDataURL('image/png').split(',')[1];
    }
    setSignatures({ ...signatures, [activeField.id]: signatureData });
    setSignatureModalOpen(false);
  };

  const handleDownloadSignedCopy = async () => {
    if (!documentData) return;
    try {
      setDownloading(true);
      const response = await ESignService.downloadSignedDocument(
        documentData.document.id,
        token
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${documentData.document.title || 'document'}_signed.pdf`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      setDownloading(false);
    } catch (err) {
      toast.error('Failed to download signed document');
      setDownloading(false);
    }
  };

const handleSubmit = async () => {
  const requiredFieldIds = documentData.fields.filter(field => field.isRequired).map(field => field.id);
  const missingRequiredFields = requiredFieldIds.filter(id => !signatures[id]);
  
  if (missingRequiredFields.length > 0) {
    toast.error(`Please sign all required fields (${missingRequiredFields.length} remaining)`);
    return;
  }
  
  try {
    setSubmitting(true);
    const signatureData = {
      signatures: Object.entries(signatures).map(([fieldId, signatureData]) => ({
        fieldId,
        signatureData
      }))
    };
    // Initiate async signing - this returns immediately
    const response = await ESignService.signDocument(
      documentData.document.id,
      token,
      signatureData
    );
    if (response.data && response.data.success) {
      // Use documentId from API response for redirect
      history.push(`/e-sign/processing/${response.data.documentId}`);
    } else {
      throw new Error('Failed to initiate signing process');
    }
    
  } catch (err) {
    toast.error(err.response?.data?.message || 'Failed to sign document');
    setSubmitting(false);
  }
};

const handleReject = async () => {
  if (!rejectionComment.trim()) {
    toast.error('Please provide a reason for rejection');
    return;
  }

  try {
    setRejecting(true);
    await ESignService.rejectDocument(documentData.document.id, token, {
      rejectionComment: rejectionComment.trim()
    });
    
    toast.success('Document rejected successfully');
    setRejectModalOpen(false);
    
    // Redirect to a completion page or show rejection confirmation
    history.push('/e-sign/rejected');
    
  } catch (err) {
    toast.error(err.response?.data?.message || 'Failed to reject document');
    setRejecting(false);
  }
};

const handleOpenRejectModal = () => {
  setRejectModalOpen(true);
  setRejectionComment('');
};

const handleCloseRejectModal = () => {
  setRejectModalOpen(false);
  setRejectionComment('');
};

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const getPagesWithSignatures = () => {
    if (!documentData?.fields) return [];
    return [...new Set(documentData.fields.map(field => field.pageNumber))].sort((a, b) => a - b);
  };

  const scrollToPage = (pageNumber) => {
    const pageElement = document.querySelector(`[data-page-number="${pageNumber}"]`);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: 'smooth' });
    }
  };
  // --- PDF Overlay Logic ---
  const renderSignatureFields = () => {
    if (!documentData || !pdfWidth || !pdfHeight) return null;

    // Create an array of signature field overlays for each page
    return Array.from(new Array(numPages), (_, pageIndex) => (
      <div
        key={`overlay_page_${pageIndex + 1}`}
        className={classes.fieldOverlay}
        style={{
          width: pdfWidth,
          height: pdfHeight,
          top: pageIndex * (pdfHeight + 16), // Add page margin offset
        }}
      >
        {documentData.fields
          .filter(field => field.pageNumber === pageIndex + 1) // Filter fields for current page
          .map(field => (
            <div
              key={field.id}
              className={classes.signatureField}
              style={{
                left: `${(field.xPosition / 100) * pdfWidth}px`,
                top: `${(field.yPosition / 100) * pdfHeight}px`,
                width: `${(field.width / 100) * pdfWidth}px`,
                height: `${(field.height / 100) * pdfHeight}px`,
              }}
              onClick={() => handleFieldClick(field)}
            >
              {signatures[field.id] && (
                <img
                  src={`data:image/png;base64,${signatures[field.id]}`}
                  alt="Signature"
                  className={classes.signatureImage}
                />
              )}
            </div>
          ))}
      </div>
    ));
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    // Show a friendlier message based on error type
    const isCompleted =
      typeof error === 'string' &&
      (error.toLowerCase().includes('completed') || error.toLowerCase().includes('already signed'));
    
    const isWaitingForTurn =
      typeof error === 'string' &&
      (error.toLowerCase().includes('not your turn') || error.toLowerCase().includes('wait for previous'));
    
    const getErrorTitle = () => {
      if (isCompleted) return 'Document Completed';
      if (isWaitingForTurn) return 'Please Wait Your Turn';
      return 'Unable to Access Document';
    };
    
    const getErrorMessage = () => {
      if (isCompleted) {
        return 'This document has already been completed and signed. No further action is required.';
      }
      if (isWaitingForTurn) {
        return 'This document is still being processed by previous signers. You will receive a notification when it\'s your turn to sign.';
      }
      return error;
    };
    
    return (
      <div className={classes.root}>
        <div className={classes.appBar}>
          <img src={cargoesLogo} alt="DP World" className={classes.logo} />
          <Typography variant="h6" className={classes.title}>
            E-SIGN
          </Typography>
        </div>
        <Container className={classes.container}>
          <Paper className={classes.paper} style={{ padding: 24 }}>
            <Typography variant="h5" color={isCompleted ? 'primary' : isWaitingForTurn ? 'textSecondary' : 'error'} gutterBottom>
              {getErrorTitle()}
            </Typography>
            <Typography>
              {getErrorMessage()}
            </Typography>
          </Paper>
        </Container>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.appBar}>
        <img src={cargoesLogo} alt="DP World" className={classes.logo} />
        <Typography variant="h6" className={classes.title}>
          E-SIGN
        </Typography>
      </div>

      <Container className={classes.container}>
        <Paper className={classes.paper}>
          <div className={classes.header}>
            <Typography variant="h6">
              {documentData.document.title}
            </Typography>
            <div className={classes.buttons}>
              {/* Not needed for now */}
              {/* <Button
                variant="outlined"
                onClick={handleDownloadSignedCopy}
                disabled={downloading}
              >
                {downloading ? <CircularProgress size={16} /> : 'Download Signed Copy'}
              </Button> */}
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleOpenRejectModal}
                disabled={submitting || rejecting}
              >
                Reject Document
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={submitting || rejecting}
              >
                {submitting ? <CircularProgress size={24} /> : 'Submit'}
              </Button>
            </div>
          </div>

          {getPagesWithSignatures().length > 0 && (
            <SignaturePageNavigation
              pages={getPagesWithSignatures()}
              onPageClick={scrollToPage}
            />
          )}

          <div className={classes.documentContainer}>
            <div className={classes.pdfWrapper} style={{
              width: pdfWidth ? pdfWidth : undefined,
              height: pdfHeight ? pdfHeight : undefined
            }}>
              <Document
                file={documentData.document.viewUrl}
                loading={<div style={{ textAlign: 'center', padding: 40 }}><CircularProgress /></div>}
                onLoadError={err => setError('Failed to load PDF')}
                onLoadSuccess={onDocumentLoadSuccess}
              >
                {numPages &&
                  Array.from(new Array(numPages), (el, index) => (
                    <div
                      key={`pdf_page_wrapper_${index + 1}`}
                      style={{
                        position: 'relative',
                        marginBottom: 40,
                        width: pdfWidth || 'auto',
                        height: pdfHeight || 'auto'
                      }}
                    >
                      <Page
                        key={`page_${index + 1}`}
                        pageNumber={index + 1}
                        data-page-number={index + 1}
                        onLoadSuccess={page => {
                          if (index === 0) {
                            setPdfWidth(page.width);
                            setPdfHeight(page.height);
                          }
                        }}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        className={classes.pdfPage}
                      />
                      {/* Signature fields for this page */}
                      <div
                        className={classes.fieldOverlay}
                        style={{
                          width: pdfWidth,
                          height: pdfHeight,
                          pointerEvents: 'none', // Let clicks pass through unless you want to interact
                          top: 0,
                          left: 0,
                        }}
                      >
                        {documentData.fields
                          .filter(field => field.pageNumber === index + 1)
                          .map(field => (
                            <div
                              key={field.id}
                              className={classes.signatureField}
                              style={{
                                left: `${(field.xPosition / 100) * pdfWidth}px`,
                                top: `${(field.yPosition / 100) * pdfHeight}px`,
                                width: `${(field.width / 100) * pdfWidth}px`,
                                height: `${(field.height / 100) * pdfHeight}px`,
                                pointerEvents: 'all', // Enable interaction for signature fields
                              }}
                              onClick={() => handleFieldClick(field)}
                            >
                              {signatures[field.id] && (
                                <img
                                  src={`data:image/png;base64,${signatures[field.id]}`}
                                  alt="Signature"
                                  className={classes.signatureImage}
                                />
                              )}
                            </div>
                          ))}
                      </div>
                      <div style={{
                        textAlign: 'center',
                        marginTop: 8,
                        color: '#888',
                        fontSize: 14,
                        marginBottom: 16
                      }}>
                        Page {index + 1} of {numPages}
                      </div>
                    </div>
                  ))
                }
              </Document>
            </div>
          </div>
        </Paper>
      </Container>

      {/* Signature Modal */}
      <Dialog
        open={signatureModalOpen}
        onClose={handleCloseSignatureModal}
        className={classes.signatureModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">E-Sign</Typography>
            <IconButton edge="end" onClick={handleCloseSignatureModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Type" />
            <Tab label="Draw" />
            <Tab label="Upload" disabled />
          </Tabs>

          {tabValue === 1 && (
            <div style={{ marginTop: 16 }}>
              <SignatureCanvas
                ref={signatureRef}
                penColor="black"
                canvasProps={{
                  width: 400,
                  height: 200,
                  style: {
                    width: '100%',
                    height: '200px',
                    border: '1px solid #e0e0e0',
                    backgroundColor: '#fff',
                    marginTop: '16px'
                  }
                }}
              />
            </div>
          )}

          {tabValue === 0 && (
            <div style={{ marginTop: 16 }}>
              <TextField
                label="Enter your full name here"
                variant="outlined"
                fullWidth
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
              />
              {/* Dynamically calculate max font size for the current text and canvas */}
              {(() => {
                function getMaxFontSize(text, width, height) {
                  let fontSize = height;
                  const ctx = document.createElement('canvas').getContext('2d');
                  while (fontSize > 4) {
                    ctx.font = `italic ${fontSize}px serif`;
                    const textWidth = ctx.measureText(text).width;
                    if (textWidth <= width - 10 && fontSize <= height * 0.9) break;
                    fontSize -= 1;
                  }
                  return fontSize;
                }
                const maxFontSize = getMaxFontSize(typedName || 'Signature', signatureCanvasWidth, signatureCanvasHeight);
                return (
                  <>
                    <Box display="flex" alignItems="center" mt={2} mb={2}>
                      <Typography variant="body2" style={{ minWidth: 80 }}>Font Size:</Typography>
                      <input
                        type="range"
                        min={8}
                        max={maxFontSize}
                        value={signatureFontSize}
                        onChange={e => setSignatureFontSize(Number(e.target.value))}
                        style={{ flex: 1, margin: '0 8px' }}
                      />
                      <Typography variant="body2" style={{ minWidth: 40 }}>{signatureFontSize}px</Typography>
                    </Box>
                    {typedName && (
                      <div
                        style={{
                          marginTop: 16,
                          padding: 0,
                          border: '1px solid #e0e0e0',
                          fontFamily: 'cursive',
                          textAlign: 'center',
                          width: signatureCanvasWidth,
                          height: signatureCanvasHeight,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'transparent',
                          marginLeft: 'auto',
                          marginRight: 'auto',
                        }}
                      >
                        <canvas
                          width={signatureCanvasWidth}
                          height={signatureCanvasHeight}
                          ref={el => {
                            if (el && typedName) {
                              const ctx = el.getContext('2d');
                              ctx.clearRect(0, 0, signatureCanvasWidth, signatureCanvasHeight);
                              ctx.font = `italic ${signatureFontSize}px serif`;
                              ctx.fillStyle = '#000';
                              ctx.textBaseline = 'middle';
                              ctx.textAlign = 'center';
                              ctx.fillText(typedName, signatureCanvasWidth / 2, signatureCanvasHeight / 2);
                            }
                          }}
                          style={{ width: signatureCanvasWidth, height: signatureCanvasHeight, background: 'transparent' }}
                        />
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSignatureModal} color="default">
            Cancel
          </Button>
          <Button onClick={handleClearSignature} color="secondary">
            Clear
          </Button>
          <Button
            onClick={handleApplySignature}
            color="primary"
            variant="contained"
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Modal */}
      <Dialog
        open={rejectModalOpen}
        onClose={handleCloseRejectModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Reject Document</Typography>
            <IconButton edge="end" onClick={handleCloseRejectModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Please provide a reason for rejecting this document. This action cannot be undone.
          </Typography>
          <TextField
            label="Reason for rejection"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={rejectionComment}
            onChange={(e) => setRejectionComment(e.target.value)}
            placeholder="Please explain why you are rejecting this document..."
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectModal} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            color="secondary"
            variant="contained"
            disabled={rejecting || !rejectionComment.trim()}
          >
            {rejecting ? <CircularProgress size={24} /> : 'Reject Document'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SigningPage;